import { Link } from '@tanstack/react-router';

import { useTeamUsage } from '@/shared/api/admin-overview';
import type { AdminTeam } from '@/shared/api/admin-teams';
import { fmtAgo, fmtBytes, fmtDateTime } from '@/shared/lib/format';
import { Card, DataRow, Muted } from '@/shared/ui/kit';
import { errorMessage } from '@/shared/lib/ui-utils';

/** Members / projects / tasks / storage / activity for the team-detail page. */
export function TeamUsageCard({ team }: { team: AdminTeam }) {
  const { data, error } = useTeamUsage(team.id);
  return (
    <Card title="Usage">
      <dl className="space-y-1.5">
        <DataRow label="Members" value={team.memberCount} />
        <DataRow label="Storage used" value={fmtBytes(team.storageUsedBytes)} />
        {error ? (
          <Muted>{errorMessage(error)}</Muted>
        ) : !data ? (
          <Muted>Loading…</Muted>
        ) : (
          <>
            <DataRow
              label="Projects"
              value={`${data.activeProjects}${data.projects > data.activeProjects ? ` (+${data.projects - data.activeProjects} archived)` : ''}`}
            />
            <DataRow label="Tasks created" value={data.tasksCreated.toLocaleString()} />
            <DataRow
              label="Last activity"
              value={<span title={fmtDateTime(data.lastActionAt)}>{fmtAgo(data.lastActionAt)}</span>}
            />
            <DataRow label="Actions, 30 days" value={data.actions30d.toLocaleString()} />
            {data.pendingUpgradeRequestId ? (
              <p className="pt-1 text-xs">
                <Link to="/upgrade-requests" className="text-amber-300 hover:underline">
                  This team has a pending upgrade request →
                </Link>
              </p>
            ) : null}
          </>
        )}
      </dl>
    </Card>
  );
}
